import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
      continue; // Don't get the children since it will reload the template
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,

      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"CDN_URL":"https://cdn.forbes.ru","DUCK_URL_SERVER":"http://api.forbes.ru/api","SERVICE_ENV":"","CP_PUBLIC_ID":"pk_e09af4dab8b903c270c375c5a8866","REQUEST_TIMEOUT":"45000","SERVER_HOST":"0.0.0.0","SERVER_PORT":"8080","npm_package_devDependencies_stylelint_config_prettier":"^8.0.2","npm_package_devDependencies__nuxt_typescript_build":"^2.1.0","npm_package_dependencies_pinia":"^2.0.13","npm_package_devDependencies_stylelint_config_idiomatic_order":"^8.1.0","npm_package_devDependencies__nuxt_types":"^2.15.8","npm_config_version_commit_hooks":"true","npm_config_user_agent":"yarn/1.22.10 npm/? node/v14.20.1 linux x64","npm_package_dependencies_abortcontroller_polyfill":"^1.7.3","npm_config_bin_links":"true","HOSTNAME":"1447a02bf106","npm_node_execpath":"/usr/bin/node","npm_package_dependencies_node_html_parser":"^4.1.4","npm_package_dependencies_node_fetch":"3.1.0","npm_config_init_version":"1.0.0","DUCK_URL":"https://www.forbes.ru/api","SHLVL":"5","npm_package_devDependencies__types_url_parse":"^1.4.4","npm_package_dependencies_vue_virtual_scroller":"^1.0.10","npm_package_dependencies_resize_observer":"^1.0.2","npm_package_dependencies__nuxtjs_router":"^1.6.1","npm_package_scripts_generate_preset":"yarn generate preset new --name","npm_package_scripts_start_staging":"NODE_ENV=staging cross-env DISABLE_V8_COMPILE_CACHE=true nuxt start","HOME":"/root","npm_package_devDependencies_sass":"^1.49.9","npm_package_devDependencies_eslint_plugin_nuxt":"^2.0.0","npm_package_dependencies_vue_concurrency":"^2.1.2","npm_package_devDependencies_eslint_config_prettier":"^8.3.0","npm_package_engines_node":"14","npm_package_dependencies_vue2_touch_events":"^3.2.2","npm_package_dependencies_smoothscroll_polyfill":"^0.4.4","npm_config_init_license":"MIT","YARN_WRAP_OUTPUT":"false","npm_config_version_tag_prefix":"v","npm_package_devDependencies_stylelint_config_standard":"^22.0.0","npm_package_scripts_lint_style":"stylelint **/*.{scss,vue} --ignore-path .gitignore","npm_package_dependencies_text_security":"^3.2.1","npm_package_description":"Brand-new Forbes website","npm_package_readmeFilename":"README.md","npm_package_dependencies_nuxt":"^2.15.8","npm_package_dependencies_base64_arraybuffer":"^1.0.2","npm_package_scripts_generate_component":"yarn generate component new --name","npm_package_lint_staged____scss_vue_":"yarn lint:style","npm_package_devDependencies__nuxtjs_style_resources":"^1.2.1","npm_package_scripts_build_staging":"NODE_ENV=staging cross-env DISABLE_V8_COMPILE_CACHE=true nuxt build","npm_package_scripts_dev":"cross-env DISABLE_V8_COMPILE_CACHE=true nuxt","npm_package_dependencies_normalize_css":"^8.0.1","npm_package_private":"true","npm_package_dependencies_throttle_debounce_ts":"^1.1.1","npm_package_dependencies_blurhash":"^1.1.4","npm_package_scripts_lint":"yarn lint:script && yarn lint:style","npm_config_registry":"https://registry.yarnpkg.com","npm_package_devDependencies_stylelint":"^13.13.1","npm_package_devDependencies__types_lodash":"^4.14.172","npm_package_devDependencies__types_vue2_datepicker":"^3.3.1","npm_package_dependencies_reconnecting_websocket":"^4.4.0","npm_package_scripts_start":"cross-env DISABLE_V8_COMPILE_CACHE=true nuxt start","npm_config_ignore_scripts":"","npm_package_dependencies_vue_lazy_hydration":"^2.0.0-beta.4","npm_package_dependencies__linusborg_vue_simple_portal":"^0.1.5","npm_package_scripts_generate_local_env":"yarn generate local-env new","npm_package_author_email":"talpa@forbes.ru","PATH":"/tmp/yarn--1754986261980-0.6810747078671806:/var/www/node_modules/.bin:/usr/local/share/.config/yarn/link/node_modules/.bin:/usr/libexec/lib/node_modules/npm/bin/node-gyp-bin:/usr/lib/node_modules/npm/bin/node-gyp-bin:/usr/bin/node_modules/npm/bin/node-gyp-bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin","NODE":"/usr/bin/node","npm_package_devDependencies__types_js_cookie":"^2.2.7","npm_package_devDependencies__nuxtjs_eslint_config_typescript":"^6.0.1","npm_package_repository_type":"git","npm_package_name":"@forbes/canary","npm_package_devDependencies__nuxtjs_composition_api":"^0.27.0","npm_package_dependencies_vue_advanced_cropper":"^1.10.1","npm_package_devDependencies_eslint":"^7.32.0","npm_package_devDependencies__vue_eslint_config_typescript":"^7.0.0","npm_package_devDependencies__types_upng_js":"^2.1.2","npm_package_dependencies_nuxt_precompress":"^0.5.9","WATERFALL_URL":"wss://waterfall.forbes.ru/ws","npm_lifecycle_script":"cross-env DISABLE_V8_COMPILE_CACHE=true nuxt build","npm_package_simple_git_hooks_pre_commit":"npx lint-staged","npm_package_devDependencies_hygen":"^6.1.0","npm_package_devDependencies__nuxtjs_pwa":"^3.3.5","npm_package_scripts_generate_page_list":"yarn generate page-list new --name","npm_package_devDependencies__vue_eslint_config_prettier":"^6.0.0","npm_package_dependencies_intersection_observer":"^0.12.0","npm_package_dependencies__drozd_nuxt_performance":"^1.0.1","npm_config_version_git_message":"v%s","npm_lifecycle_event":"build","npm_package_version":"","npm_package_devDependencies_nuxt_build_optimisations":"^1.0.6","npm_package_dependencies__nuxtjs_dayjs":"^1.4.0","npm_package_repository_url":"git@gitlab.com:forbes/canary.git","npm_config_argv":"{\"remain\":[],\"cooked\":[\"run\",\"build\"],\"original\":[\"build\"]}","npm_package_devDependencies_simple_git_hooks":"^2.6.1","npm_package_devDependencies_lint_staged":"^11.1.2","npm_package_scripts_build":"cross-env DISABLE_V8_COMPILE_CACHE=true nuxt build","npm_package_dependencies_nuxt_viewport":"^0.0.7","npm_config__forbes_registry":"https://verdaccio-npm-repo.forbes.ru","DISABLE_V8_COMPILE_CACHE":"true","npm_package_dependencies_vue2_datepicker":"^3.11.1","npm_package_dependencies__pinia_nuxt":"0.2.1","npm_package_scripts_killport":"npx kill-port 8080","npm_config_version_git_tag":"true","npm_config_version_git_sign":"","npm_package_scripts_generate":"cross-env HYGEN_TMPLS=.templates hygen","npm_package_license":"UNLICENSED","npm_config_strict_ssl":"true","npm_package_devDependencies_eslint_plugin_prettier":"^4.0.0","npm_package_devDependencies__types_node_fetch":"^2.5.12","npm_package_dependencies_js_cookie":"^3.0.0","npm_package_scripts_svg_optimize":"svgo --config='.svgo.config.json' ./src/assets/icons","npm_package_scripts_lint_script":"eslint --ext .ts,.vue --ignore-path .gitignore .","npm_package_scripts_generate_page":"yarn generate page new --name","PWD":"/var/www","npm_execpath":"/usr/share/node_modules/yarn/bin/yarn.js","npm_package_devDependencies__nuxtjs_svg":"^0.4.1","npm_package_devDependencies_eslint_plugin_prettier_vue":"^3.1.0","npm_package_devDependencies__nuxtjs_stylelint_module":"^4.0.0","npm_package_dependencies__nuxtjs_device":"^2.1.0","npm_package_author_name":"Igor Talpa","npm_package_lint_staged____ts_vue_":"yarn lint:script","npm_package_devDependencies_cross_env":"^7.0.3","npm_package_dependencies_upng_js":"^2.1.0","npm_config_save_prefix":"^","npm_config_ignore_optional":"","npm_package_devDependencies__types_smoothscroll_polyfill":"^0.3.1","npm_package_dependencies_nuxt_health":"^0.1.0","npm_package_dependencies_maska":"^2.1.7","npm_package_dependencies__nuxt_typescript_runtime":"^2.0.1","npm_package_devDependencies_ts_essentials":"^8.1.0","npm_package_dependencies__forbes_openapi":"^0.0.216","INIT_CWD":"/var/www","NODE_ENV":"production","_applied":"true","HOST":"https://www.forbes.ru"}
    }
    // Only set once

    if (context.req) {
      app.context.req = context.req
    }
    if (context.res) {
      app.context.res = context.res
    }

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
          window.location.replace(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
